import { IGatsbyExtractedAnimation } from "@bond-london/gatsby-transformer-extracted-lottie";
import { IGatsbySvg } from "@bond-london/gatsby-transformer-extracted-svg/src/types";
import { getRTFInformation } from "@bond-london/graphcms-rich-text";
import classNames from "classnames";
import { graphql } from "gatsby";
import { IGatsbyImageData } from "gatsby-plugin-image";
import React from "react";
import ReactPlayer from "react-player/lazy";
import { LinkInformation } from "../elements/Links";
import { ThumbnailLink } from "../elements/ThumbnailLink";
import { isVideoUrl } from "../elements/renderers/RTFLinkRenderer";
import { lookupColourString } from "../lookups";
import { IVideo } from "../shared/Visual";
import { SectionPaddingClassName } from "../styles/index";
import { BannerVideo } from "./BannerVideo";
import { BlockCollection } from "./BlockCollection";
import { BodyText } from "./BodyText";
import { BodyTextAside } from "./BodyTextAside";
import { ComplexHero } from "./ComplexHero";
import { ContentBlock } from "./ContentBlock";
import { DynamicHero } from "./DynamicHero";
import { Feature } from "./Feature";
import { FormComponent } from "./FormComponent";
import { FullWidthPageLink } from "./FullWidthPageLink";
import { ImageBlock } from "./ImageBlock";
import { KeyFeatures } from "./KeyFeatures";
import { ListItem } from "./ListItem";
import { MultiQuotes } from "./MultiQuotes";
import { PageCard } from "./PageCard";
import { PageLink } from "./PageLink";
import { Quote } from "./Quote";
import { SectionIntro } from "./SectionIntro";
import { ServicesSlide } from "./ServicesSlide";
import { Slide } from "./Slide";
import { Slideshow } from "./Slideshow/Slideshow";
import { SmallSectionHero } from "./SmallSectionHero";
import { Statement } from "./Statement";
import { TextHighlight } from "./TextHighlight";
import { TextIntro } from "./TextIntro";
import { Unlock } from "./Unlock";
import { VideoWithLink } from "./VideoWithLink";

function pageTypeToPrefix(pageType: Queries.GraphCMS_PageType | null): string {
  if (pageType) {
    return `/${pageType}/`;
  }
  return "/";
}

function resolveSlug(prefix: string, slug: string | undefined | null) {
  if (slug) {
    if (slug === "index") return "/";
    return prefix + slug;
  }
  return "/";
}

function processLink<T>(
  pageLink: Queries.PageLinkFullFragment | null,
  handler: (
    content: Queries.LinkedPageFragment,
    prefix: string
  ) => T | undefined
) {
  if (pageLink?.page) {
    return handler(pageLink.page, pageTypeToPrefix(pageLink.page.pageType));
  }
  return undefined;
}

export function getVideo(fragment: Queries.VideoFragment | undefined | null) {
  if (
    fragment?.mimeType?.startsWith("video/") &&
    !!fragment?.localFile?.publicURL
  ) {
    return fragment as IVideo;
  }
  return undefined;
}

export function getImage(fragment: Queries.ImageFragment | undefined | null) {
  return fragment?.gatsbyImageData ?? undefined;
  // return fragment?.localFile?.childImageSharp?.gatsbyImageData;
}

export function getSvg(fragment: Queries.SvgFragment | undefined | null) {
  return fragment?.localFile?.childGatsbySvg
    ?.extracted as unknown as IGatsbySvg;
}

function getLottie(fragment: Queries.LottieFragment | undefined | null) {
  return fragment?.localFile?.childGatsbyAnimation
    ?.extracted as unknown as IGatsbyExtractedAnimation;
}

export function getVisual(
  fragment:
    | Queries.ImageFragment
    | Queries.SvgFragment
    | Queries.LottieFragment
    | Queries.VideoFragment
    | undefined
    | null
) {
  return (
    (getImage(fragment as Queries.ImageFragment) ??
      getSvg(fragment as Queries.SvgFragment)) ||
    getLottie(fragment as Queries.LottieFragment) ||
    getVideo(fragment as Queries.VideoFragment)
  );
}

export function getAlt(
  image: Queries.ImageFragment | undefined | null,
  option: string | undefined | null
) {
  return image?.alternateText ?? option ?? "";
}

export function getLinkForPage(page: Queries.LinkedPageFragment): string {
  const prefix = pageTypeToPrefix(page.pageType);
  return resolveSlug(prefix, page.slug);
}

export function getImageForPage(
  page: Queries.DirectPageFragment
): IGatsbyImageData | undefined {
  const link = page.pageLinks?.length ? page.pageLinks[0] : undefined;
  const image = page.heroImage ?? link?.image;
  return getImage(image);
}

function getPossibleLinkForPage(page: Queries.LinkedPageFragment | null) {
  if (page) {
    return getLinkForPage(page);
  }
  return undefined;
}

export function getLink(page: Queries.PageLinkFullFragment | null): string {
  return (
    processLink<string>(page, (content, prefix) =>
      resolveSlug(prefix, content.slug)
    ) ?? "/"
  );
}

export function getLinkFromContentBlock(
  block: Queries.ContentBlockFragment
): string | undefined {
  if (block.page) {
    return getLinkForPage(block.page);
  }
  if (block.pageLink) {
    return getLink(block.pageLink);
  }
  if (block.internalLink) {
    return getLinkForPage(block.internalLink);
  }
  return undefined;
}

export function getLinkTitleFromContentBlock(
  block: Queries.ContentBlockFragment
): string | undefined | null {
  if (block.pageLink) {
    return block.pageLink.linkTitle;
  }
  if (block.page) {
    return block.page.linkTitle;
  }
  if (block.internalLink) {
    return block.internalLink.linkTitle;
  }
  return undefined;
}

export function getLinkTitle(
  pageLink: Queries.PageLinkFullFragment | null
): string | undefined {
  if (pageLink?.title) {
    return pageLink.title;
  }

  return processLink(pageLink, (page) => page.title);
}

function getLinkAsset(pageLink: Queries.PageLinkFullFragment | null) {
  if (pageLink?.image) {
    return pageLink.image;
  }
  if (pageLink?.page) {
    return pageLink.page.heroImage;
  }
  return undefined;
}

function getLinkTagline(
  pageLink: Queries.PageLinkFullFragment | null
): string | undefined {
  if (pageLink?.tagline) {
    return pageLink.tagline;
  }
  return undefined;
}

export function getPageCategory(
  page: Queries.LinkedPageFragment
): string | undefined {
  return page.pageCategory?.length ? page.pageCategory[0] : undefined;
}

function getLinkCategory(
  pageLink: Queries.PageLinkFullFragment | null
): string | undefined {
  return processLink(pageLink, getPageCategory);
}

function getLinkMessage(pageLink: Queries.PageLinkFullFragment | null) {
  if (pageLink?.message) {
    return getRTFInformation(pageLink.message);
  }
  return undefined;
}

function buildPageCardFromContentBlock(block: Queries.ContentBlockFragment) {
  return (
    <PageCard
      key={block.id}
      image={getImage(block.image)}
      alt={getAlt(block.image, block.title || "Image")}
      title={block.title}
      tagline={block.heading}
      fadeOut={true}
      message={getRTFInformation(block.message)}
      link={getLinkFromContentBlock(block)}
      linkTitle={getLinkTitleFromContentBlock(block)}
      showLinkAsButton={block.showLinkAsButton}
    />
  );
}

function buildImageBlock(imageBlock: Queries.ImageBlockFragment) {
  return (
    <ImageBlock
      key={imageBlock.id}
      image={getImage(imageBlock.image)}
      shape={getSvg(imageBlock.image)}
      alt={getAlt(imageBlock.image, "Image")}
      fullWidth={imageBlock.fullWidth}
    />
  );
}

function buildPageLink(
  link: Queries.PageLinkFullFragment,
  fullWidth?: boolean | null
) {
  const image = getLinkAsset(link);
  const title = getLinkTitle(link) ?? "Link";
  const Component = fullWidth ? FullWidthPageLink : PageLink;
  return (
    <Component
      key={link.id}
      image={getImage(image)}
      alt={getAlt(image, title)}
      title={title}
      heading={getLinkTagline(link)}
      message={getLinkMessage(link)}
      link={getLink(link)}
      linkTitle={link.linkTitle}
    />
  );
}

function buildStatementContentBlock(
  block: Queries.ContentBlockFragment,
  quoteTitle: boolean
) {
  return (
    <Statement
      key={block.id}
      title={block.title}
      heading={block.heading}
      message={getRTFInformation(block.message)}
      quoteTitle={quoteTitle}
      className={classNames(
        block.backgroundColour &&
          lookupColourString(block.backgroundColour, "bg")
      )}
    />
  );
}

function buildQuoteContentBlock(block: Queries.ContentBlockFragment) {
  return <Quote key={block.id} title={block.title} author={block.author} />;
}

function buildLinkFromExternalLink(
  externalLink: Queries.ExternalLinkFragment
): LinkInformation {
  return { title: externalLink.title, external: externalLink.url };
}

// eslint-disable-next-line import/no-unused-modules
export function buildLinksFromPageLinkFull(
  link: Queries.PageLinkFullFragment
): Array<LinkInformation> | undefined {
  const results = [];
  if (link.externalLinks) {
    link.externalLinks.forEach((e) =>
      results.push(buildLinkFromExternalLink(e))
    );
  }
  if (
    link.page ??
    (link.linkTitle && (!link.externalLinks || link.externalLinks.length === 0))
  ) {
    results.push({
      title: link.linkTitle,
      internal: link.page ? getLinkForPage(link.page) : "/",
    });
  }
  if (results.length > 0) return results;
  return undefined;
}

export function buildThumbnailLink(
  link: Queries.PageLinkFullFragment
): JSX.Element {
  const image = getLinkAsset(link);
  const title = getLinkTitle(link) ?? "Link";
  return (
    <ThumbnailLink
      key={link.id}
      image={getImage(image)}
      alt={getAlt(image, title)}
      title={title}
      tagline={getLinkCategory(link) ?? getLinkTagline(link) ?? "Missing"}
      message={getLinkMessage(link)}
      links={buildLinksFromPageLinkFull(link)}
    />
  );
}

function buildSectionIntroContentBlock(block: Queries.ContentBlockFragment) {
  return (
    <SectionIntro key={block.id} title={block.title} heading={block.heading} />
  );
}

function buildSmallSectionHeroContentBlock(
  block: Queries.ContentBlockFragment
) {
  return (
    <SmallSectionHero
      key={block.id}
      title={block.title}
      heading={block.heading}
      image={getImage(block.image)}
      svg={getSvg(block.image)}
      alt={getAlt(block.image, block.title || "Hero")}
      message={getRTFInformation(block.message)}
    />
  );
}

function buildDynamicHero(block: Queries.ContentBlockFragment) {
  return (
    <DynamicHero
      key={block.id}
      firstAsset={getVisual(block.image)}
      secondAsset={getVisual(block.animation)}
    />
  );
}

function buildGenericContentBlock(block: Queries.ContentBlockFragment) {
  const bgClassName = block.backgroundColour
    ? lookupColourString(block.backgroundColour, "bg")
    : "";
  const textClassName = block.backgroundColour
    ? lookupColourString(block.backgroundColour, "textForBg")
    : block.gradientColour
    ? lookupColourString(block.gradientColour, "textForBg")
    : "";
  const heading = block.heading ?? block.title;
  const title = block.heading ? block.title : undefined;
  return (
    <ContentBlock
      key={block.id}
      heading={heading}
      title={title}
      image={getImage(block.image)}
      animation={getLottie(block.animation)}
      svg={getSvg(block.image)}
      alt={getAlt(block.image, "Sub Section Hero Image")}
      message={getRTFInformation(block.message)}
      link={getLinkFromContentBlock(block)}
      linkTitle={getLinkTitleFromContentBlock(block)}
      className={classNames(bgClassName, textClassName)}
      gradientType={block.gradientType}
      gradientColour={block.gradientColour}
    />
  );
}

export function calcBlockIndex(
  blockType: Queries.GraphCMS_ContentType | null,
  blockTypeCount: Record<string, number>
) {
  if (!blockType) {
    return 1;
  }
  const current = blockTypeCount[blockType] || 0;
  const next = current + 1;
  blockTypeCount[blockType] = next;
  return next;
}

export function buildContentBlock(
  block: Queries.ContentBlockFragment,
  index: number,
  blockTypeCount: number,
  ownerType?: Queries.GraphCMS_CollectionType | null,
  page?: Queries.PageFragment,
  collectionSize?: number
): JSX.Element {
  switch (ownerType) {
    case "NumberedList":
      return buildListContentBlock(block);
  }

  switch (block.contentBlockType) {
    case "Slide":
      if (ownerType === "ServicesCarousel") {
        return buildServicesSlideContentBlock(block, index);
      }
      return buildSlideContentBlock(block, index, collectionSize);

    case "BannerVideo":
      return buildBannerVideo(block);
    case "DynamicHero":
      return buildDynamicHero(block);
    case "TextHighlight":
      return buildTextHighlightContentBlock(block);
    case "TextIntro":
      return buildTextIntroContentBlock(block);
    case "Unlock":
      return buildUnlockContentBlock(block);
    case "BodyText":
      return buildBodyTextContentBlock(block, blockTypeCount, page);
    case "Quote":
      return buildQuoteContentBlock(block);
    case "Feature":
      return buildFeatureContentBlock(block);
    case "SectionIntro":
      return buildSectionIntroContentBlock(block);
    case "SmallSectionHero":
      return buildSmallSectionHeroContentBlock(block);
    case "Statement":
    case "StatementQuote":
      return buildStatementContentBlock(
        block,
        block.contentBlockType === "StatementQuote"
      );
    case "PageCard":
      return buildPageCardFromContentBlock(block);
    case "VideoWithLink":
      return buildVideoWithLinkFromContentBlock(block);
    case "ComplexHero":
      return buildComplexHero(block);
  }

  return buildGenericContentBlock(block);
}

function buildBodyTextContentBlock(
  {
    id,
    title,
    heading,
    message,
    aside,
    date,
    author,
    backgroundColour,
  }: Queries.ContentBlockFragment,
  typeIndex: number,
  page?: Queries.PageFragment
) {
  // console.log("aside data:", aside); // Add this

  const realAuthor = author ?? page?.author;
  const realDate = date ?? page?.date;
  const sideText = getRTFInformation(aside);
  const messageText = getRTFInformation(message);

  if (sideText) {
    const headingOrTitle = heading ?? title;
    return (
      <BodyTextAside
        key={id}
        heading={realAuthor ?? headingOrTitle}
        body={messageText}
        sideText={sideText}
        date={typeIndex < 2 ? realDate : undefined}
        backgroundColour={backgroundColour}
      />
    );
  }

  if ((realDate && typeIndex < 1) ?? realAuthor) {
    return (
      <BodyTextAside
        key={id}
        heading={realAuthor}
        body={getRTFInformation(message)}
        date={realDate}
        backgroundColour={backgroundColour}
      />
    );
  }
  return (
    <BodyText
      key={id}
      body={getRTFInformation(message)}
      backgroundColour={backgroundColour}
      typeIndex={typeIndex}
    />
  );
}

function buildFeatureContentBlock({
  id,
  title,
  message,
  backgroundShape,
}: Queries.ContentBlockFragment) {
  return (
    <Feature
      key={id}
      title={title}
      message={getRTFInformation(message)}
      shape={getSvg(backgroundShape)}
    />
  );
}

function buildServicesSlideContentBlock(
  block: Queries.ContentBlockFragment,
  index: number
) {
  const headline = block.heading ?? `${index + 1}`.padStart(2, "0");
  const message = getRTFInformation(block.message);
  const shape = getSvg(block.backgroundShape);

  return (
    <ServicesSlide
      key={block.id}
      id={block.id}
      title={block.title}
      headline={headline}
      message={message}
      shape={shape}
      background={block.backgroundColour}
      link={getLinkFromContentBlock(block)}
      linkTitle={getLinkTitleFromContentBlock(block)}
      showLinkAsButton={block.showLinkAsButton}
    />
  );
}

function buildBannerVideo(block: Queries.ContentBlockFragment) {
  const message = getRTFInformation(block.message);
  return (
    <BannerVideo
      key={block.id}
      heading={block.heading}
      message={message}
      link={getLinkFromContentBlock(block)}
      linkTitle={getLinkTitleFromContentBlock(block)}
      visual={getVisual(block.animation)}
    />
  );
}

function buildSlideContentBlock(
  block: Queries.ContentBlockFragment,
  index: number,
  collectionSize?: number
) {
  const headline =
    block.heading ??
    `${index + 1}`.padStart(2, "0") +
      "/" +
      `${collectionSize ?? ""}`.padStart(2, "0");
  const message = getRTFInformation(block.message);
  const shape = getSvg(block.backgroundShape);
  const imageShape = getSvg(block.image);
  const image = getImage(block.image);
  const background = block.backgroundColour ?? "Lazer";

  if (image ?? imageShape) {
    return (
      <Slide
        key={block.id}
        id={block.id}
        title={block.title}
        headline={headline}
        message={message}
        shape={shape}
        imageShape={imageShape}
        image={image}
        alt={getAlt(block.image, "Slide")}
        background={background}
        link={getLinkFromContentBlock(block)}
        linkTitle={getLinkTitleFromContentBlock(block)}
      />
    );
  }
  return (
    <ServicesSlide
      key={block.id}
      id={block.id}
      title={block.title}
      headline={headline}
      message={message}
      shape={shape}
      background={background}
      link={getLinkFromContentBlock(block)}
      linkTitle={getLinkTitleFromContentBlock(block)}
      showLinkAsButton={block.showLinkAsButton}
    />
  );
}

function buildTextHighlightContentBlock({
  id,
  message,
  backgroundShape,
  aside,
}: Queries.ContentBlockFragment) {
  return (
    <TextHighlight
      key={id}
      text={getRTFInformation(message)}
      shape={getSvg(backgroundShape)}
      aside={getRTFInformation(aside)}
    />
  );
}

function buildTextIntroContentBlock({
  id,
  message,
  title,
  heading,
  backgroundShape,
  showTitle,
}: Queries.ContentBlockFragment) {
  return (
    <TextIntro
      key={id}
      title={showTitle ? heading ?? title : undefined}
      message={getRTFInformation(message)}
      shape={getSvg(backgroundShape)}
    />
  );
}

function buildUnlockContentBlock(block: Queries.ContentBlockFragment) {
  const { id, title, heading, message, image } = block;
  return (
    <Unlock
      key={id}
      title={title}
      heading={heading}
      message={getRTFInformation(message)}
      internalLink={getLinkFromContentBlock(block)}
      image={getSvg(image)}
    />
  );
}

function buildListContentBlock({
  id,
  title,
  message,
}: Queries.ContentBlockFragment) {
  return (
    <ListItem key={id} title={title} message={getRTFInformation(message)} />
  );
}

function buildCollection(
  page: Queries.PageFragment,
  collection: Queries.CollectionFragment
) {
  switch (collection.collectionType) {
    case "Slideshow":
      return <Slideshow key={collection.id} collection={collection} />;
    case "KeyFeatures":
      return <KeyFeatures key={collection.id} collection={collection} />;
    case "MultiQuotes":
      return <MultiQuotes key={collection.id} collection={collection} />;
  }

  return (
    <BlockCollection key={collection.id} page={page} collection={collection} />
  );
}

function buildVideoWithLinkFromContentBlock(
  block: Queries.ContentBlockFragment
) {
  const {
    id,
    // image,
    animation,
    internalLink,
    externalLink,
    title,
    showLinkAsButton,
    showTitle,
    heading,
  } = block;
  const visual = getVisual(animation); // , true, image);
  if (!visual) {
    if (externalLink && isVideoUrl(externalLink)) {
      return (
        <section
          className={classNames(
            "page-grid max-width relative",
            SectionPaddingClassName
          )}
        >
          <div className="aspect-h-9 aspect-w-16 col-span-4 col-start-2 md:col-span-8 md:col-start-2 lg:col-span-12 lg:col-start-3">
            <ReactPlayer
              url={externalLink}
              playsinline={true}
              width="100%"
              height="100%"
              playing={true}
              light
              controls
            />
          </div>
        </section>
      );
    }
    return (
      <pre>
        VideoWithLink content block needs an animation or external link to
        Vimeo/YouTube video.
      </pre>
    );
  }

  // const realVisual: VisualAsset = { ...visual, dontCrop: true };
  return (
    <VideoWithLink
      visual={visual}
      key={id}
      internalLink={getPossibleLinkForPage(internalLink)}
      linkTitle={internalLink?.linkTitle}
      showLinkAsButton={showLinkAsButton}
      subHeading={showTitle ? title : undefined}
      heading={heading}
    />
  );
}

function buildComplexHero({
  id,
  title,
  heading,
  image,
  gradientColour,
  backgroundColour,
  externalLink,
  showLinkAsButton,
  pageLink,
}: Queries.ContentBlockFragment) {
  return (
    <ComplexHero
      id={id}
      title={title}
      heading={heading}
      image={getVisual(image)}
      gradientColour={gradientColour}
      backgroundColour={backgroundColour}
      linkTo={externalLink}
      linkAsButton={showLinkAsButton}
      linkTitle={pageLink?.title}
    />
  );
}

export const Blocks: React.FC<{
  blocks: Queries.PageFragment["blocks"];
  page: Queries.PageFragment;
}> = ({ blocks, page }) => {
  const blockTypeCount: Record<string, number> = {};
  return (
    <>
      {blocks.map((block) => {
        switch (block.__typename) {
          case "GraphCMS_ImageBlock":
            return buildImageBlock(block);
          case "GraphCMS_PageLink":
            return buildPageLink(block, page.fullWidthPageLinks);
          case "GraphCMS_ContentBlock":
            return buildContentBlock(
              block,
              0,
              calcBlockIndex(block.contentBlockType, blockTypeCount),
              null,
              page,
              blocks.length
            );
          case "GraphCMS_Collection":
            return buildCollection(page, block);
          case "GraphCMS_FormComponent":
            return (
              <FormComponent
                controlInformation={block.controlInformation}
                backgroundColour={block.backgroundColour}
              />
            );
        }
        return undefined;
      })}
    </>
  );
};

export const ButtonLinkFragment = graphql`
  fragment ButtonLink on GraphCMS_ButtonLink {
    __typename
    id
    remoteId
    buttonText
    page {
      ...LinkedPage
    }
    externalLink
    backgroundColor
    textColor
    locale
  }
`;

// eslint-disable-next-line import/no-unused-modules
export const ImageBlockFragment = graphql`
  fragment ImageBlock on GraphCMS_ImageBlock {
    id
    image {
      ...FullWidthImage
      ...Svg
    }
    fullWidth
  }
`;

// eslint-disable-next-line import/no-unused-modules
export const FormComponentFragment = graphql`
  fragment FormComponent on GraphCMS_FormComponent {
    id
    formName: name
    controlInformation {
      name
      controlInformation
    }
    backgroundColour
  }
`;

// eslint-disable-next-line import/no-unused-modules
export const ContentBlockFragment = graphql`
  fragment ContentBlock on GraphCMS_ContentBlock {
    id
    contentBlockType
    title
    showTitle
    heading
    image {
      ...FullWidthImage
      ...Svg
      ...Video
    }
    animation {
      ...Lottie
      ...Video
    }
    message {
      cleaned
      references {
        ...Video
        ...DownloadableAsset
        ...LinkedPage
        ...ButtonLink
      }
    }
    aside {
      cleaned
      references {
        ...Video
        ...DownloadableAsset
        ...LinkedPage
        ...ButtonLink
      }
    }
    pageLink {
      ...PageLinkFull
    }
    page {
      ...LinkedPage
    }
    externalLink
    backgroundShape {
      ...Svg
    }
    backgroundColour
    gradientType
    gradientColour
    date(formatString: "MMMM D YYYY")
    author
    internalLink {
      ...LinkedPage
    }
    showLinkAsButton
  }
`;

// eslint-disable-next-line import/no-unused-modules
export const LinkedPageFragment = graphql`
  fragment LinkedPage on GraphCMS_Page {
    id
    remoteId
    heroImage {
      ...Image
    }
    title
    linkTitle
    slug
    pageType
    pageCategory
    summary {
      cleaned
    }
    locale
  }
`;

// eslint-disable-next-line import/no-unused-modules
export const DirectPageFrament = graphql`
  fragment DirectPage on GraphCMS_Page {
    ...LinkedPage
    pageLinks {
      ...PageLinkOnly
    }
  }
`;

// eslint-disable-next-line import/no-unused-modules
export const PageLinkOnlyFragment = graphql`
  fragment PageLinkOnly on GraphCMS_PageLink {
    id
    image {
      ...Image
    }
    title
    linkTitle
    tagline
    message {
      cleaned
      references {
        ...Video
        ...DownloadableAsset
        ...LinkedPage
      }
    }
  }
`;

// eslint-disable-next-line import/no-unused-modules
export const PageLinkFullFragment = graphql`
  fragment PageLinkFull on GraphCMS_PageLink {
    ...PageLinkOnly
    page {
      ...LinkedPage
    }
    externalLinks {
      ...ExternalLink
    }
  }
`;

// eslint-disable-next-line import/no-unused-modules
export const StatementFragment = graphql`
  fragment Statement on GraphCMS_Statement {
    id
    title
    heading
    description
  }
`;

// eslint-disable-next-line import/no-unused-modules
export const PersonFragment = graphql`
  fragment Person on GraphCMS_Person {
    id
    name
    image {
      ...Image
    }
    jobPosition
    email
    phone
    description
  }
`;

// eslint-disable-next-line import/no-unused-modules
export const CollectionFragment = graphql`
  fragment Collection on GraphCMS_Collection {
    id
    name
    displayTitle
    showTitle
    showSummary
    forceScroll
    collectionType
    backgroundShape {
      ...Svg
    }
    backgroundImage {
      ...Image
      ...Svg
    }
    message {
      cleaned
    }
    backgroundColour
    contents {
      __typename
      ...PageLinkFull
      ...Person
      ...ContentBlock
      ...DirectPage
    }
    pageTypeFilter
    pageFilterCount
    showFilter
    showLargeFirstResult
    page {
      ...LinkedPage
    }
    contactForm {
      id
      name
      controlInformation
    }
  }
`;
